import './TestInfo.scss';
import { useSupportEmail } from '@/contexts/SupportEmail/SupportEmailContext';
import { StageCandidateActionType } from 'enums/stageAction';
import { SupportedLocale, TestStatus } from 'generated/graphql';
import { FC, ReactNode, useState } from 'react';
import { isMobile as isUsingMobile } from 'react-device-detect';
import { Trans, useTranslation } from 'react-i18next';
import { hasLanguageDisclaimer } from 'helpers/stageActions.helpers';
import NeedExtraTimeModal from 'views/Test/NeedExtraTimeModal/NeedExtraTimeModal';
import { ISingleTestInfoConfigNote } from 'views/Test/TestInfo/TestInfo.constants';
import {
  Button,
  InlineNotification,
  Link,
  NotificationType,
  SelectFormField,
  Tag,
  TagColor,
  TagSize,
  TSelectOption,
} from '@spotted-zebra-uk/ui-components';
import PracticeTestIntro from './PracticeTestIntro/PracticeTestIntro';
import TestInfoNote from './TestInfoNote/TestInfoNote';

interface ITestIntro {
  MainIcon: FC<{ alt: string }>;
  title: string;
  notes: ISingleTestInfoConfigNote[];
  testStatus: TestStatus;
  hasPractice: boolean;
  onStart: (locale?: SupportedLocale) => void;
  onStartPracticeTest?: (locale?: SupportedLocale) => void;
  testTimePerQuestion: number;
  isMobileAllowed?: boolean;
  type?: StageCandidateActionType;
  assessmentLocale: SupportedLocale;
  selectedLocales?: SupportedLocale[];
  companyName?: string;
  showPracticeTestIntro?: boolean;
  displayPracticeTestIntro?: (newValue: boolean) => void;
  primaryButtonText?: string;
  // Replaces the default start button; rendered below the accessibility notice.
  customActions?: ReactNode;
  // Integrity monitoring camera gate: when provided and not yet granted, the
  // CTA is disabled and a notice with a re-request link is shown above it.
  cameraPermission?: {
    granted: boolean;
    request: () => void;
  };
}

const ENGLISH_ONLY_ASSESSMENT_TYPES = [
  StageCandidateActionType.CognitiveNumericalTest,
  StageCandidateActionType.CognitiveVerablTest,
  StageCandidateActionType.CodingExternalAssessment,
  StageCandidateActionType.EnglishProficiencyTest,
];

const TestIntro: FC<ITestIntro> = ({
  MainIcon,
  title,
  notes,
  testStatus,
  hasPractice,
  onStart,
  onStartPracticeTest,
  testTimePerQuestion,
  showPracticeTestIntro,
  displayPracticeTestIntro,
  type,
  assessmentLocale,
  selectedLocales = [],
  companyName,
  isMobileAllowed = true,
  primaryButtonText,
  customActions,
  cameraPermission,
}) => {
  const { t } = useTranslation();
  const { supportEmail, supportEmailHref } = useSupportEmail();
  const [showModal, setShowModal] = useState(false);
  const isPreferredLocaleAvailable = selectedLocales.includes(assessmentLocale);
  const [selectedLocale, setSelectedLocale] = useState<
    SupportedLocale | undefined
  >(() =>
    selectedLocales.length > 1 && isPreferredLocaleAvailable
      ? assessmentLocale
      : undefined
  );
  const [hasLocaleError, setHasLocaleError] = useState(false);
  const actions = {
    openModal: () => setShowModal(true),
  };

  const availableLocaleOptions: TSelectOption<SupportedLocale>[] =
    selectedLocales.map(locale => ({
      label: t(`common.languageNames.${locale}`),
      value: locale,
    }));
  const shouldShowLocaleSelector = availableLocaleOptions.length > 1;
  const resolvedAssessmentLocale =
    selectedLocale ||
    (selectedLocales.length === 1 ? selectedLocales[0] : undefined);
  const selectedLocaleOption = availableLocaleOptions.find(
    option => option.value === selectedLocale
  );
  const isDisabledOnMobile = isUsingMobile && !isMobileAllowed;
  const hasLocaleGuidance =
    type && hasLanguageDisclaimer(assessmentLocale, [type]);
  const showEnglishOnlyNotice =
    assessmentLocale !== SupportedLocale.English &&
    !!type &&
    ENGLISH_ONLY_ASSESSMENT_TYPES.includes(type);
  const mandatedLocale =
    type === StageCandidateActionType.PersonalityTest &&
    selectedLocales.length === 1
      ? selectedLocales[0]
      : undefined;
  const showMandatedLocaleNotice =
    !!mandatedLocale && mandatedLocale !== assessmentLocale;
  const mandatedLanguageName = mandatedLocale
    ? t(`common.languageNames.${mandatedLocale}`)
    : undefined;

  const getCTAButtonText = () => {
    if (primaryButtonText) {
      return primaryButtonText;
    }

    if (testStatus !== TestStatus.NotStarted) {
      return t('test.instructions.general.continue');
    }

    return hasPractice
      ? t('test.instructions.general.startPracticeTest')
      : t('test.instructions.general.startNow');
  };

  const handleStartClick = () => {
    if (shouldShowLocaleSelector && !selectedLocale) {
      setHasLocaleError(true);
      return;
    }

    if (
      hasPractice &&
      displayPracticeTestIntro &&
      testStatus === TestStatus.NotStarted
    ) {
      displayPracticeTestIntro(true);
      return;
    }

    onStart(resolvedAssessmentLocale);
  };

  const handleStartPracticeTest = () => {
    onStartPracticeTest?.(resolvedAssessmentLocale);
  };

  const handleLocaleChange = (option: TSelectOption<SupportedLocale>) => {
    setSelectedLocale(option.value);
    setHasLocaleError(false);
  };

  return showPracticeTestIntro ? (
    <PracticeTestIntro
      testName={title}
      type={type as StageCandidateActionType}
      onStartPracticeTest={handleStartPracticeTest}
      onStartAssessment={() => onStart(resolvedAssessmentLocale)}
      displayPracticeTestIntro={displayPracticeTestIntro}
    />
  ) : (
    <main>
      <div className="TestInfo">
        <div className="TestInfo__Content">
          <div className="TestInfo__Content__Top">
            <div className="TestInfo__Content__Top__Main">
              <MainIcon alt="" />
              <h1 className="TestInfo__Content__Top__Main__Title">{title}</h1>
              {(hasLocaleGuidance || showMandatedLocaleNotice) && (
                <Tag
                  isPresentational={true}
                  isDismissible={false}
                  tagColor={TagColor.DISABLED}
                  size={TagSize.LARGE}
                  containerClass="TestInfo__Content__Top__Main__LocaleGuidance"
                >
                  {showMandatedLocaleNotice
                    ? t('stage.localesSupport.testLanguageTag', {
                        language: mandatedLanguageName,
                      })
                    : t(`stage.localesSupport.introGuidance`)}
                </Tag>
              )}
            </div>
            <div className="TestInfo__Content__Top__NotesList">
              {notes.map(note => {
                const AdditionalItem = note.extraTimeConfig ? (
                  <Button
                    variant="tertiary"
                    className="TestInfo__AdditionalItem"
                    onClick={actions[note.extraTimeConfig.action]}
                    aria-label={t('test.testTimeLimitExtraTime', {
                      minutes: testTimePerQuestion,
                    })}
                  >
                    {note.extraTimeConfig.text}
                  </Button>
                ) : null;
                return (
                  <TestInfoNote
                    key={note.id}
                    icon={note.icon}
                    text={note.text}
                    additionalItem={AdditionalItem}
                    className={note.className}
                    alt={note.alt}
                    dataTestid={note.dataTestid}
                    accessibilityAdjustment={
                      note?.accessibilityAdjustment
                        ? t('test.instructions.general.extraTime', {
                            amount: note.accessibilityAdjustment,
                          })
                        : null
                    }
                  />
                );
              })}
            </div>
          </div>

          <div className="TestInfo__Content__Bottom">
            {isDisabledOnMobile && (
              <div className="TestInfo__Content__MobileDisclaimer">
                <InlineNotification
                  notificationType={NotificationType.ERROR}
                  title={t('test.instructions.coding.mobile_disclaimer_title')}
                  className="TestInfo__Content__MobileDisclaimer__Notification"
                >
                  <p>{t('test.instructions.coding.mobile_disclaimer_body')}</p>
                </InlineNotification>
              </div>
            )}

            {showMandatedLocaleNotice && (
              <InlineNotification
                notificationType={NotificationType.INFORMATION}
                title={t('stage.localesSupport.mandatedLocaleNotice.title', {
                  language: mandatedLanguageName,
                })}
                className="TestInfo__Content__Bottom__LocaleNotice"
                dataTestId="test-intro__mandated-locale-notice"
              >
                <p>
                  {t('stage.localesSupport.mandatedLocaleNotice.body', {
                    companyName,
                  })}
                </p>
              </InlineNotification>
            )}

            {shouldShowLocaleSelector && (
              <InlineNotification
                notificationType={NotificationType.INFORMATION}
                title={
                  isPreferredLocaleAvailable
                    ? t('test.languagePreference.confirmTitle')
                    : t('test.languagePreference.title')
                }
                className="TestInfo__Content__Bottom__LanguagePreference"
                classNameTitle="TestInfo__Content__Bottom__LanguagePreference__Title"
                dataTestId="test-intro__language-preference"
              >
                <div className="TestInfo__Content__Bottom__LanguagePreference__Content">
                  <p className="TestInfo__Content__Bottom__LanguagePreference__Body">
                    {t('test.languagePreference.body')}
                  </p>
                  <SelectFormField<SupportedLocale>
                    id="test-intro-language-preference"
                    label={t('test.languagePreference.fieldLabel')}
                    options={availableLocaleOptions}
                    value={selectedLocaleOption}
                    hasError={hasLocaleError}
                    bottomText={
                      hasLocaleError
                        ? t('test.languagePreference.required')
                        : undefined
                    }
                    placeholder={t('test.languagePreference.fieldLabel')}
                    ariaLabel={t('test.languagePreference.fieldLabel')}
                    hasClearIndicator={false}
                    onChange={handleLocaleChange}
                  />
                </div>
              </InlineNotification>
            )}

            {showEnglishOnlyNotice && (
              <InlineNotification
                notificationType={NotificationType.INFORMATION}
                title={t('stage.localesSupport.englishOnlyNotice.title')}
                className="TestInfo__Content__Bottom__LocaleNotice"
                dataTestId="test-intro__english-only-notice"
              >
                <p>{t('stage.localesSupport.englishOnlyNotice.body')}</p>
              </InlineNotification>
            )}

            <p className="TestInfo__Content__Bottom__AccessibilityNotice">
              <Trans
                i18nKey="test.accessibilityNotice.text"
                t={t}
                components={[
                  <Link
                    key={supportEmail}
                    to={supportEmailHref}
                    target="_blank"
                    className="TestInfo__Content__Bottom__AccessibilityNotice__Link"
                    aria-label={t('common.sendEmailTo', {
                      email: supportEmail,
                    })}
                    data-testid="test-intro__support-email-link"
                  />,
                ]}
              />
            </p>
            {customActions}
          </div>
          <div className="TestInfo__Content__Middle">
            {cameraPermission && !cameraPermission.granted && (
              <p className="TestInfo__Content__Middle__CameraNotice">
                {t('test.integrityMonitoring.cameraRequired')}{' '}
                <Button
                  variant="tertiary"
                  className="TestInfo__Content__Middle__CameraNotice__Button"
                  onClick={cameraPermission.request}
                >
                  {t('test.integrityMonitoring.allowCamera')}
                </Button>
              </p>
            )}
            {!customActions && (
              <Button
                onClick={handleStartClick}
                disabled={
                  isDisabledOnMobile ||
                  (cameraPermission && !cameraPermission.granted)
                }
                className="TestInfo__Content__Middle__Button"
                data-testid="test-intro__start-button"
                aria-label={
                  testStatus === TestStatus.NotStarted
                    ? t('test.startTestName', { testTitle: title })
                    : t('test.continueTestName', { testTitle: title })
                }
              >
                {getCTAButtonText()}
              </Button>
            )}
          </div>
        </div>
      </div>
      <NeedExtraTimeModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </main>
  );
};

export default TestIntro;
